/* eslint-disable max-lines */
import uuid from '@utils/uuid';
import { schema } from 'normalizr';
import orderBy from 'lodash/orderBy';
import paginateAction from '@utils/paginateAction';
import { quizTypes } from '../constants';
import { paginatorListSchema, questionSchema } from '../schemas';

const replaceQuizEndpoint = (endpoint = '', isSubjective = false) => {
  if (isSubjective) {
    return endpoint
      .replace(/\/quiz\//, '/sts-quiz/')
      .replace(/\/session\//, '/sts-session/');
  }

  return endpoint;
};

export const fetchQuizDetails = (quizUid, isSubjective = false) => (dispatch) =>
  dispatch({
    type: quizTypes.FETCH_QUIZ_DETAILS,
    endpoint: replaceQuizEndpoint(
      `v2/quizzes/quiz/${quizUid}/details/`,
      isSubjective
    ),
    method: 'get',
    quizUid,
    schema: new schema.Object({}),
    processData: (data) => {
      let { sections } = data;
      const calculators = {};
      const max_answerable_question = {};

      const hasOneSection = sections.length === 1;
      // max_answerable_question === 0 means attempt all

      sections.forEach((section) => {
        calculators[section.uid] = section.calculator_type;

        if (!hasOneSection) {
          max_answerable_question[section.uid] =
            section?.max_answerable_question || section.questions_count;
        }
      });
      if (hasOneSection) {
        calculators.default = sections[0].calculator_type;
        max_answerable_question.default =
          sections[0]?.max_answerable_question || sections[0].questions_count;
        sections = [];
      }
      return { ...data, sections, calculators, max_answerable_question };
    }
  });

export const fetchMyQuizAttempts = (quizUid) => (dispatch) =>
  dispatch({
    type: quizTypes.FETCH_MY_QUIZ_ATTEMPTS,
    endpoint: `v1/quizzes/quiz/${quizUid}/leaderboard/?limit=200`,
    method: 'get',
    quizUid,
    schema: paginatorListSchema
  });

const getQuizLeaderboard = (key, isLive, resetWithoutEmpty) => (next) => ({
  type: isLive
    ? quizTypes.FETCH_QUIZ_LEADERBOARD_LIVE
    : quizTypes.FETCH_QUIZ_LEADERBOARD_ALL,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  processData(result) {
    // generate uid for key of LeaderboardCard component
    const modResult =
      result?.results.map((resultElem) => {
        return {
          ...resultElem,
          session: resultElem.uid,
          uid: uuid()
        };
      }) || [];
    return {
      ...result,
      results: orderBy(modResult, 'rank', 'asc'),
      userData: result?.results?.user_rank
    };
  }
});

export const fetchQuizLeaderBoard = (
  quizUid,
  isLive,
  nextPage,
  resetWithoutEmpty = false,
  isSubjective = false
) => {
  const key = isLive ? 'quizLeaderboardLive' : 'quizLeaderboardAll';
  return paginateAction(
    replaceQuizEndpoint(
      `v2/quizzes/quiz/${quizUid}/get_quiz_leaderboard/?is_live=${
        isLive ? 1 : 0
      }&limit=30&offset=0`,
      isSubjective
    ),
    getQuizLeaderboard(quizUid, isLive, resetWithoutEmpty),
    key,
    quizUid,
    resetWithoutEmpty
  )(nextPage);
};

export const fetchQuizRank = (quizUid, isLiveFlag, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_QUIZ_RANK,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/quiz/${quizUid}/get_user_rank/?is_live=${isLiveFlag ? 1 : 0}`,
      isSubjective
    ),
    method: 'get'
  });

export const enrollInQuiz = (quizUid, language, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.ENROLL_IN_QUIZ,
    endpoint: replaceQuizEndpoint(
      `v2/quizzes/quiz/${quizUid}/enroll/`,
      isSubjective
    ),
    method: 'post',
    data: { language },
    schema: new schema.Object({
      question_with_user_choices: [{ question: questionSchema }]
    }),
    processData: (result) => {
      const newQuestions = result.question_with_user_choices.map((item) => {
        return { question: item };
      });

      return {
        ...result,
        question_with_user_choices: newQuestions
      };
    }
  });

export const fetchQuestions = (sessionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_QUESTIONS,
    endpoint: replaceQuizEndpoint(
      `v2/quizzes/session/${sessionUid}/get_questions/`,
      isSubjective
    ),
    method: 'get',
    sessionUid,
    schema: new schema.Object({
      question_with_user_choices: [{ question: questionSchema }]
    }),
    processData: (result) => {
      const newQuestions = result.question_with_user_choices.map((item) => {
        return { question: item };
      });

      return {
        ...result,
        question_with_user_choices: newQuestions
      };
    }
  });

export const sendViewedQuestion = (
  sessionUid,
  questionUid,
  isSubjective = false
) => (dispatch) =>
  dispatch({
    type: quizTypes.SEND_VIEWED_QUESTION,
    endpoint: replaceQuizEndpoint(
      `v2/quizzes/session/${sessionUid}/viewed_question/`,
      isSubjective
    ),
    method: 'post',
    schema: new schema.Object({}),
    data: {
      question_uid: questionUid
    }
  });

export const finishQuiz = (sessionUid, isSubjective = false) => (dispatch) =>
  dispatch({
    type: quizTypes.FINISH_QUIZ,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/finish_quiz/`,
      isSubjective
    ),
    method: 'post',
    schema: new schema.Object({})
  });

export const finishSection = (sessionUid, sectionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FINISH_SECTION,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/finish_section/`,
      isSubjective
    ),
    method: 'post',
    sectionUid,
    data: { section_uid: sectionUid },
    schema: new schema.Object({})
  });

export const toggleMarkQuestion = (
  sessionUid,
  questionUid,
  isMarked,
  isSubjective = false
) => (dispatch) =>
  dispatch({
    type: quizTypes.MARK_QUESTION,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/mark_unmark_question/`,
      isSubjective
    ),
    method: 'post',
    questionUid,
    isMarked,
    data: {
      q_uid: questionUid,
      is_marked: isMarked ? 1 : 0
    }
  });

export const submitAnswerResponse = (
  sessionUid,
  responseData,
  isFirstAttempt = false,
  addToBulkQueue = false,
  isSubjective = false
) => (dispatch) =>
  dispatch({
    type: quizTypes.SUBMIT_QUESTION_RESPONSE,
    endpoint: replaceQuizEndpoint(
      `v2/quizzes/session/${sessionUid}/question_response/`,
      isSubjective
    ),
    method: 'post',
    questionUid: responseData.question_uid,
    responseData,
    addToBulkQueue,
    isFirstAttempt,
    data: responseData
  });

export const updateQuestionCount = (sectionUID = 'default', value) => ({
  type: quizTypes.UPDATE_QUESTION_COUNT,
  sectionUID,
  value
});

export const updateAttemptedQuestionCount = (data = {}) => ({
  type: quizTypes.SET_QUESTION_COUNT,
  data
});

export const updateSessionLanguage = (sessionUid, languageCode) => (dispatch) =>
  dispatch({
    type: quizTypes.UPDATE_SESSION_LANGUAGE,
    endpoint: `v1/quizzes/session/${sessionUid}/update_language/`,
    method: 'post',
    data: { language: languageCode }
  });

export const bulkSubmitAnswers = (
  sessionUid,
  attemptedQuestions,
  bulkSubmitQueue,
  isSubjective = false
) => (dispatch) => {
  const data = {
    question_response: attemptedQuestions.map(
      (uid) => bulkSubmitQueue[uid].answerResponse
    )
  };
  return dispatch({
    type: quizTypes.BULK_SUBMIT_ANSWERS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/bulk_submit_question_response/`,
      isSubjective
    ),
    method: 'post',
    data,
    attemptedQuestions,
    bulkSubmitQueue
  });
};

export const getQuestionReportOptions = () => (dispatch) =>
  dispatch({
    type: quizTypes.FETCH_QUESTION_REPORT_OPTIONS,
    endpoint: 'v1/feedback/question/',
    method: 'get'
  });

export const getQuizDetailedResults = (sessionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_QUIZ_DETAILED_RESULTS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/get_detailed_elastic_analytics_report/`,
      isSubjective
    ),
    method: 'get'
  });

export const getQuizResults = (sessionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_QUIZ_RESULTS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/get_elastic_analytics_report/`,
      isSubjective
    ),
    method: 'get'
  });

export const fetchQuizSolutionFilters = (sessionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_SOLUTION_FILTERS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/get_filter/`,
      isSubjective
    ),
    method: 'get'
  });

const getQuizSolutions = (sessionUid, filtersObj, resetWithoutEmpty) => (
  next
) => ({
  type: quizTypes.FETCH_PAGINATED_SOLUTIONS,
  endpoint: next,
  method: 'get',
  key: sessionUid,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'question'
});

export const fetchQuizSolutionsWithFilters = (
  sessionUid,
  filtersObj,
  nextPage,
  resetWithoutEmpty = false,
  isSectionWise,
  isSubjective = false
) =>
  paginateAction(
    replaceQuizEndpoint(
      `v2/quizzes/session/${sessionUid}/get_questions_paginated_with_attempts/?attempt_type=${
        filtersObj.solutionType
      }&${isSectionWise ? 'section_uid' : 'topic_group'}=${
        filtersObj.sectionUid
      }&topic_uid=${filtersObj.topicUid}&limit=10`,
      isSubjective
    ),
    getQuizSolutions(sessionUid, filtersObj, resetWithoutEmpty),
    'quizSolutions',
    sessionUid,
    resetWithoutEmpty
  )(nextPage);

export const fetchTestFeedbackOptions = (quizUid) => (dispatch) =>
  dispatch({
    type: quizTypes.FETCH_TEST_FEEDBACK_OPTIONS,
    endpoint: `v1/feedback/test/?object_uid=${quizUid}`,
    method: 'get'
  });

export const fetchSessionDetails = (sessionUid, isSubjective = false) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.FETCH_SESSION_DETAILS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/details/`,
      isSubjective
    ),
    method: 'get'
  });

export const fetchSolutionFeedbackOptions = () => (dispatch) =>
  dispatch({
    type: quizTypes.FETCH_SOLUTION_FEEDBACK_OPTIONS,
    endpoint: 'v1/feedback/question_solution/',
    method: 'get'
  });

// quiz creation -->

export const checkUserAccess = (quizUID) => ({
  type: quizTypes.CHECK_ACCESS,
  endpoint: `v1/quizzes/me/${quizUID}/check_access/`,
  method: 'get'
});

export const fetchQuizData = (quizUID, store = true) => ({
  type: quizTypes.FETCH_QUIZ_DATA,
  endpoint: `v2/quizzes/me/details/${quizUID}/`,
  method: 'get',
  quizUID,
  store
});

export const fetchSessionQuestions = (quizUID, sectionUID, allIssues = 1) => ({
  type: quizTypes.FETCH_SESSION_QUESTIONS,
  endpoint: `v1/quizzes/me/${quizUID}/section_question_list/?section_uid=${sectionUID}&all_issues=${allIssues}`,
  method: 'get',
  quizUID,
  sectionUID
});

export const createTest = (data, uid) => (dispatch) =>
  dispatch({
    type: quizTypes.EDIT_QUIZ,
    endpoint: `v2/quizzes/me/edit/${uid}/`,
    method: 'post',
    data
  });

export const uploadCSV = (file, quizUID) => (dispatch) => {
  const data = new FormData();
  data.append('file', file);
  data.append('quiz_uid', quizUID);

  return dispatch({
    type: quizTypes.UPLOAD_CSV,
    endpoint: 'v1/quizzes/quiz-question/upload/',
    method: 'post',
    isFormData: true,
    data
  });
};

export const fetchReviewStats = (goalUID) => ({
  type: quizTypes.FETCH_REVIEW_STATS,
  endpoint: `v1/quizzes/quiz-review/get_review_stats/?goal_uid=${goalUID}`,
  method: 'get',
  goalUID,
  schema: new schema.Object({}),
  processData: (data) => ({
    todayCount: data.today_count,
    thisMonthCount: data.this_month_count,
    lastMonthCount: data.last_month_count
  })
});

const getReviews = (key, resetWithoutEmpty) => (next) => ({
  type: quizTypes.FETCH_REVIEWS,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchReviews = (
  goalUID,
  filter,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/quizzes/quiz-review/get_reviews/?goal_uid=${goalUID}&status_filter=${filter}&limit=4`,
    getReviews(`${goalUID}_${filter}`, resetWithoutEmpty),
    'reviewTests',
    `${goalUID}_${filter}`,
    resetWithoutEmpty
  )(nextPage);

export const finishReview = (quiz_uid, reviewUID) => ({
  type: quizTypes.FINISH_REVIEW,
  endpoint: 'v1/quizzes/quiz-review/finish_review/',
  method: 'post',
  data: { quiz_uid },
  reviewUID
});

export const createUpdateIssue = (
  quiz_uid,
  question_uid,
  value,
  issue_uid,
  description
) => ({
  type: quizTypes.CREATE_UPDATE_ISSUES,
  endpoint: 'v1/quizzes/quiz-review/create_update_issue/',
  method: 'post',
  data: {
    quiz_uid,
    question_uid,
    description,
    value,
    issue_uid
  }
});

export const markPDFUploaded = (quizUID, pdf_url) => ({
  type: quizTypes.MARK_PDF_UPLOADED,
  endpoint: `v2/quizzes/me/upload_subjective_test_pdf/${quizUID}/`,
  method: 'post',
  pdf_url,
  data: { pdf_url },
  quizUID
});

export const deleteSubjectivePDF = (quizUID, pdf_url) => ({
  type: quizTypes.DELETE_SUBJECTIVE_PDF,
  endpoint: `v2/quizzes/me/delete_subjective_test_pdf/${quizUID}/`,
  method: 'post',
  pdf_url,
  data: { pdf_url },
  quizUID
});

export const fetchReviewDashboardGoals = () => ({
  type: quizTypes.FETCH_DASHBOARD_GOALS,
  endpoint: 'v1/quizzes/quiz-review/dashbooard_goals/',
  method: 'get'
});

export const updateDashboardFilters = (goal, state, sort, language) => ({
  type: quizTypes.UPDATE_DASHBOARD_FILTERS,
  goal,
  state,
  sort,
  language
});

export const resolveIssues = (resolve_issue_uid, unresolve_issue_uid) => ({
  type: quizTypes.RESOLVE_ISSUES,
  endpoint: 'v1/quizzes/quiz-review/resolve_issue/',
  method: 'post',
  data: { resolve_issue_uid, unresolve_issue_uid }
});

export const fetchReviewDetails = (reviewUID) => ({
  type: quizTypes.FETCH_REVIEW_DETAILS,
  endpoint: `v1/quizzes/quiz-review/review_details/?review_uid=${reviewUID}`,
  method: 'get',
  reviewUID
});

export const fetchPostReviewNotifications = (quizUID) => ({
  type: quizTypes.FETCH_POSTREVIEW_NOTIFICATIONS,
  endpoint: `v1/quizzes/quiz-review/review_notification/?quiz_uid=${quizUID}`,
  method: 'get'
});

export const assignReview = (quiz_uid, reviewer_email, self_review = 0) => ({
  type: quizTypes.ASSIGN_REVIEW,
  endpoint: 'v1/quizzes/quiz-review/assign_review/',
  method: 'post',
  data: {
    reviewer_email,
    quiz_uid,
    self_review
  }
});

export const assignEvalutor = (quiz_uid, email) => ({
  type: quizTypes.ASSIGN_EVALUATOR,
  endpoint: 'v1/quizzes/subjective-test/assign_quiz/',
  method: 'post',
  data: {
    quiz_uid,
    email
  }
});

export const assignUploader = (quiz_uid, email) => ({
  type: quizTypes.ASSIGN_UPLOADER,
  endpoint: 'v1/quizzes/quiz-upload/assign_uploader/',
  method: 'post',
  data: {
    quiz_uid,
    uploader_email: email
  }
});

export const fetchReviewNotifications = (quizUID) => ({
  type: quizTypes.FETCH_REVIEW_NOTIFICATIONS,
  endpoint: `v1/quizzes/quiz-review/review_notification/?quiz_uid=${quizUID}`,
  method: 'get',
  quizUID
});

export const rejectAnswerUpload = (sessionUid, isSubjective = true) => (
  dispatch
) =>
  dispatch({
    type: quizTypes.REJECT_UPLOAD_ANSWERS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/reject_upload/`,
      isSubjective
    ),
    method: 'post'
  });

export const uploadSubjectiveAnswer = (
  sessionUid,
  urls,
  isSubjective = true
) => (dispatch) =>
  dispatch({
    type: quizTypes.UPLOAD_SUJECTIVE_ANSWERS,
    endpoint: replaceQuizEndpoint(
      `v1/quizzes/session/${sessionUid}/upload_answers/`,
      isSubjective
    ),
    method: 'post',
    data: {
      s3url_list: urls
    }
  });

export const getEvaluatedSheet = (sessionUid, isSubjective = true) => ({
  type: quizTypes.FETCH_EVALUATED_SHEET,
  endpoint: replaceQuizEndpoint(
    `v1/quizzes/session/${sessionUid}/get_evaluated_sheet/`,
    isSubjective
  ),
  method: 'get'
});
export const fetchTestEvalDashStats = (goalUID) => ({
  type: quizTypes.FETCH_EVAL_DASH_STATS,
  endpoint: `v1/quizzes/subjective-test/get_statistics/?goal_uid=${goalUID}`,
  method: 'get',
  goalUID,
  schema: new schema.Object({}),
  processData: (data) => ({
    todayCount: data.today_count,
    thisMonthCount: data.this_month_count,
    lastMonthCount: data.last_month_count
  })
});

const getMyEvals = (key, resetWithoutEmpty) => (next) => ({
  type: quizTypes.FETCH_MY_EVALS,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchMyEvals = (
  goalUID,
  filter,
  language,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/quizzes/subjective-test/get_my_evaluations/?goal_uid=${goalUID}&status_filter=${filter}&language_code=${language}&limit=4`,
    getMyEvals(`${goalUID}_${filter}_${language}`, resetWithoutEmpty),
    'myEvaluations',
    `${goalUID}_${filter}_${language}`,
    resetWithoutEmpty
  )(nextPage);

const getEvaluatedCopies = (key, resetWithoutEmpty) => (next) => ({
  type: quizTypes.FETCH_EVALUATED_COPIES,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchEvaluatedCopies = (
  quizUID,
  sortBy,
  language,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/quizzes/subjective-test/view_completed_evaluations/?quiz_uid=${quizUID}&sort_by=${sortBy}&language_code=${language}`,
    getEvaluatedCopies(`${quizUID}_${sortBy}`, resetWithoutEmpty),
    'evaluatedCopies',
    `${quizUID}_${sortBy}`,
    resetWithoutEmpty
  )(nextPage);

const getEvalQuestions = (key, resetWithoutEmpty) => (next) => ({
  type: quizTypes.FETCH_EVAL_QUESTIONS,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchEvalQuestions = (
  evalUID,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/quizzes/subjective-test/get_evaluation_questions/?evaluation_uid=${evalUID}`,
    getEvalQuestions(evalUID, resetWithoutEmpty),
    'evaluationQuestions',
    evalUID,
    resetWithoutEmpty
  )(nextPage);

export const fetchEvalDashboardGoals = () => ({
  type: quizTypes.FETCH_EVAL_DASH_GOALS,
  endpoint: 'v1/quizzes/subjective-test/get_dashboard_goals/',
  method: 'get'
});

export const fetchEvaluationDetails = (evaluationUID) => ({
  type: quizTypes.FETCH_EVAL_DETAILS,
  endpoint: `v1/quizzes/subjective-test/get_evaluation_details/?evaluation_uid=${evaluationUID}`,
  method: 'get',
  evaluationUID
});

export const assignEvaluation = (quiz_uid) => ({
  type: quizTypes.ASSIGN_EVALUATION,
  endpoint: 'v1/quizzes/subjective-test/assign_evaluation/',
  method: 'post',
  data: { quiz_uid }
});

export const fetchTestSeriesData = (testSeriesKey) => ({
  type: quizTypes.FETCH_TEST_SERIES_DATA,
  endpoint: `api/test-series/${testSeriesKey}/`,
  method: 'get',
  isCMS: true,
  testSeriesKey
});

export const fetchSubjectiveEvalFeedbackChoices = () => ({
  type: quizTypes.FETCH_FEEDBACK_CHOICES,
  endpoint: 'v1/quizzes/subjective-test/show_feedback_choices/?feedback_type=1',
  method: 'get'
});

export const fetchEvaluationCriteria = (evalUID) => ({
  type: quizTypes.FETCH_EVALUATION_CRITERIA,
  endpoint: `v1/quizzes/subjective-test/show_evaluation_criteria/?evaluation_uid=${evalUID}`,
  method: 'get',
  evalUID
});

export const skipEvaluationFeedback = (
  evaluation_uid,
  choice,
  description
) => ({
  type: quizTypes.SKIP_EVALUATION_FEEDBACK,
  endpoint: 'v1/quizzes/subjective-test/skip_evaluation_feedback/',
  method: 'post',
  data: { evaluation_uid, choice, description }
});

export const markQuestionSkipped = (evalUID, value, questionUID) => ({
  type: quizTypes.MARK_SKIPPED,
  key: evalUID,
  value,
  questionUID
});

export const assignMarks = (evalUID, value, questionUID) => ({
  type: quizTypes.ASSIGN_MARKS,
  key: evalUID,
  value,
  questionUID
});

export const submitScore = (evaluation_uid, attributes) => ({
  type: quizTypes.SUBMIT_SCORE,
  endpoint: 'v1/quizzes/subjective-test/submit_scores/',
  method: 'post',
  data: { evaluation_uid, attributes }
});

export const submitCriteriaScore = (
  evaluation_uid,
  criteria,
  remarks,
  evaluated_sheet_url
) => ({
  type: quizTypes.SUBMIT_EVALUATION_SCORE,
  endpoint: 'v1/quizzes/subjective-test/give_criteria_score/',
  method: 'post',
  data: {
    evaluation_uid,
    attributes: { criteria },
    remarks,
    evaluated_sheet_url
  }
});

export const fetchQuizUploadDashboardGoals = () => ({
  type: quizTypes.FETCH_QUIZ_UPLOAD_DASHBOARD_GOALS,
  endpoint: 'v1/quizzes/quiz-upload/dashboard_goals/',
  method: 'get'
});

const getMyUploads = (key, resetWithoutEmpty) => (next) => ({
  type: quizTypes.FETCH_MY_UPLOADS,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchMyUploads = (
  goalUID,
  filter,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/quizzes/quiz-upload/my_uploads/?goal_uid=${goalUID}&status_filter=${filter}&limit=6`,
    getMyUploads(`${goalUID}_${filter}`, resetWithoutEmpty),
    'myUploads',
    `${goalUID}_${filter}`,
    resetWithoutEmpty
  )(nextPage);

export const updateQuizInstructions = (quizUID, instructions) => ({
  type: quizTypes.UPDATE_QUIZ_INSTRUCTIONS,
  data: {
    quizUID,
    instructions
  }
});
